// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.
//
// This software is the confidential and proprietary information of
// Signal from Noise, Inc. You shall not disclose such Confidential Information
// and shall use it only in accordance with the terms of a separate license
// agreement entered into with Signal from Noise, Inc.
//
// Authors: /AUTHORS

import { dispatchGlobalEvent } from "@storytell/events";
import { PromptBarMode, useAppUI } from "~/components/layouts/minimal/AppUIContext";
import { authGateStatus } from "~/config/authGating";
import { useSafeI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

interface UseFileDropHandlersOptions {
  canUploadAssets: () => boolean;
  openKnowledgePaneAfterUpload?: boolean;
}

/**
 * Hook that provides reusable drag-and-drop handlers for file uploads.
 * These handlers activate the dropzone mode and handle file drops consistently.
 *
 * @example
 * ```tsx
 * const handlers = useFileDropHandlers({
 *   canUploadAssets: () => wire.grants.canCreate("cp.assets"),
 * });
 *
 * return (
 *   <div
 *     onDragEnter={handlers.onDragEnter}
 *     onDragOver={handlers.onDragOver}
 *     onDragLeave={handlers.onDragLeave}
 *     onDrop={handlers.onDrop}
 *   >
 *     ...
 *   </div>
 * );
 * ```
 */
export function useFileDropHandlers(options: UseFileDropHandlersOptions) {
  const { actions } = useAppUI();
  const wire = useWire();
  const { t } = useSafeI18n();
  const { canUploadAssets, openKnowledgePaneAfterUpload = false } = options;

  const onDragEnter = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes("Files") && canUploadAssets()) {
      e.preventDefault();
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  const onDragOver = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes("Files") && canUploadAssets()) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  const onDragLeave = (e: DragEvent) => {
    // Only hide dropzone when truly leaving the window
    // This prevents flickering when dragging over child elements
    if (e.clientX === 0 && e.clientY === 0) {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!canUploadAssets()) {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
      return;
    }

    if (wire.auth?.isGuest?.()) {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
      wire.ui.authGate.openModal({
        title: t("modal_signup_create_skill_title"),
        description: t("modal_signup_create_skill_desc"),
      });
      return;
    }

    // Gate uploads behind auth when configured
    const uploadGate = authGateStatus(wire.auth, "uploadAssets");
    if (uploadGate === "pending") {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
      return;
    }
    if (uploadGate === "gated") {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
      wire.ui.authGate.openModal({
        title: t("modal_signup_upload_title"),
        description: t("modal_signup_upload_desc"),
        pendingAction: { type: "upload" },
        feature: "uploadAssets",
      });
      return;
    }

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
      trackFeature({ feature: "nav.drop_upload", via: "drag" });

      // The dropzone overlay covers the page, so the prompt bar under the pointer is found by
      // position rather than by the drop target.
      const promptBar = document.elementsFromPoint(e.clientX, e.clientY).find((el) => el.id === "prompt-bar");
      if (promptBar) {
        dispatchGlobalEvent("drop-files-on-prompt-bar", {
          files,
          target: promptBar,
          openKnowledgePane: openKnowledgePaneAfterUpload,
        });
      } else {
        dispatchGlobalEvent("upload-files", { files, openKnowledgePane: openKnowledgePaneAfterUpload });
      }
    } else {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
    }
  };

  return {
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop: handleDrop,
  };
}
